import { SvgIconsMap } from '@design/design-tokens/iconography/SvgIcons';
import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

const iconSvgTemplates = new Map<string, SVGSVGElement>();

const emptySvgIcon = (): SVGSVGElement =>
  document.createElementNS('http://www.w3.org/2000/svg', 'svg');

interface CloneDesignSystemIconArgs {
  iconName: keyof typeof SvgIconsMap;
  size: number;
  className: string;
  viewBox?: string;
  fillColor?: string;
}

/** Render a design-system SVGR icon once, then clone for contenteditable chips. */
export const cloneDesignSystemIcon = ({
  iconName,
  size,
  className,
  viewBox = '0 0 40 40',
  fillColor,
}: CloneDesignSystemIconArgs): SVGSVGElement => {
  const cacheKey = `${iconName}:${size}:${className}:${viewBox}:${fillColor ?? ''}`;
  let template = iconSvgTemplates.get(cacheKey);

  if (!template) {
    const host = document.createElement('span');
    const root = createRoot(host);
    flushSync(() => {
      root.render(
        createElement(SvgIconsMap[iconName], {
          width: size,
          height: size,
          viewBox,
          className,
          fill: fillColor,
          // `className`'s `fill: currentColor` (a CSS class rule) otherwise
          // beats the `fill` presentation attribute above for any icon whose
          // path doesn't already hardcode its own color — inline `style`
          // has higher specificity than an external class, so it wins.
          style: fillColor ? { fill: fillColor } : undefined,
          'aria-hidden': true,
        })
      );
    });
    const svg = host.querySelector('svg');
    root.unmount();
    template = svg instanceof SVGSVGElement ? svg : emptySvgIcon();
    iconSvgTemplates.set(cacheKey, template);
  }

  const clone = template.cloneNode(true);
  return clone instanceof SVGSVGElement ? clone : emptySvgIcon();
};
